/**
 * PlayerAssetsTable — V2 list body for the player-asset catalogs, built on
 * the shared LibTable (theme-token surfaces, hover and borders).
 *
 * Preview column:
 *   • image types — a small tile of the thumbnail / image (avatars sit on the
 *     app's default player background, like the Customize screen tile)
 *   • app music   — a play/pause button; one shared <audio> so only one
 *     track plays at a time (same approach as BackgroundMusicTableV2)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetchPlayerAssets, deletePlayerAsset, reorderPlayerAssets } from "../../../actions/playerAssetActions";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import { UNCONFIRMED_MESSAGE } from "../../../constants/playerAssetTypes";
import { LibTable, IconButton, StatusPill, Button, cn } from "../../redesign";
import { APP_COLORS } from "./PlayerAssetPreviewCard";

// Pure reorder computation, kept separate from the component so it can be
// unit-tested without simulating a real dnd-kit pointer/keyboard drag (which
// is unreliable in jsdom). Returns the reordered list, or null when the drop
// did not actually change the order (e.g. dropped back where it started, or
// either id is not in the list) — callers use null to skip the save.
export function moveAsset(list, activeId, overId) {
  if (activeId == null || overId == null || activeId === overId) return null;
  const oldIndex = list.findIndex(asset => asset.id === activeId);
  const newIndex = list.findIndex(asset => asset.id === overId);
  if (oldIndex < 0 || newIndex < 0) return null;
  return arrayMove(list, oldIndex, newIndex);
}

const DragHandleIcon = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="9" cy="6" r="0.5" />
    <circle cx="9" cy="12" r="0.5" />
    <circle cx="9" cy="18" r="0.5" />
    <circle cx="15" cy="6" r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const DeleteIconSvg = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const ImageGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const PlayGlyph = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseGlyph = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <rect x="6" y="5" width="4" height="14" />
    <rect x="14" y="5" width="4" height="14" />
  </svg>
);

/* ─── Preview cells ────────────────────────────────────────────────── */

function ImageThumb({ asset, type }) {
  const src = asset.thumbnail_url || asset.image_url;
  const isAvatar = type === "avatar";
  return (
    <span
      className={cn(
        "inline-grid place-items-center overflow-hidden align-middle border border-sand",
        !isAvatar && "bg-cream text-text-muted"
      )}
      style={{
        width: 40,
        height: 48,
        borderRadius: 6,
        // Avatars sit on the app's default player background (fixed app color).
        background: isAvatar ? APP_COLORS.playerDefaultBackground : undefined,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={asset.name}
          style={{ width: "100%", height: "100%", objectFit: isAvatar ? "contain" : "cover" }}
        />
      ) : isAvatar ? (
        <span style={{ color: APP_COLORS.onMedia, fontSize: 8, fontWeight: 600, textAlign: "center" }}>
          No
          <br />
          Avatar
        </span>
      ) : (
        ImageGlyph
      )}
    </span>
  );
}

function PlayButton({ playing, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={playing ? "Pause preview" : "Play preview"}
      className={cn(
        "inline-grid place-items-center align-middle border-0 cursor-pointer",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        playing
          ? "bg-teal-green text-white"
          : "bg-teal-green-soft text-teal-green-dark hover:bg-teal-green hover:text-white"
      )}
      style={{ width: 30, height: 30, borderRadius: "50%", transition: "all 140ms ease" }}
    >
      {playing ? PauseGlyph : PlayGlyph}
    </button>
  );
}

/* ─── Sortable row ─────────────────────────────────────────────────── */

function SortableAssetRow({
  asset,
  type,
  position,
  isMusic,
  playingId,
  onTogglePreview,
  editPath,
  onEditClick,
  onDelete,
  disabled,
}) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: asset.id,
    disabled,
  });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row ref={setNodeRef} style={dragStyle} inactive={!asset.is_active}>
      <LibTable.Td style={{ paddingTop: 8, paddingBottom: 8, textAlign: "center" }}>
        {/* `listeners` only here so the rest of the row stays clickable. */}
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...(disabled ? {} : listeners)}
          aria-label="Drag to reorder"
          className={cn(
            "inline-grid place-items-center text-text-muted",
            disabled
              ? "cursor-not-allowed opacity-40"
              : "cursor-grab active:cursor-grabbing hover:bg-cream hover:text-navy"
          )}
          style={{ width: 26, height: 26, borderRadius: 4, transition: "all 140ms ease", touchAction: "none" }}
        >
          {DragHandleIcon}
        </span>
      </LibTable.Td>
      <LibTable.Td style={{ paddingTop: 8, paddingBottom: 8 }}>
        {isMusic ? (
          <PlayButton
            playing={playingId === asset.id}
            disabled={!asset.audio_url}
            onClick={() => onTogglePreview(asset)}
          />
        ) : (
          <ImageThumb asset={asset} type={type} />
        )}
      </LibTable.Td>
      <LibTable.Td>
        <Link
          to={editPath}
          className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
          style={{ fontSize: 13.5 }}
          title={asset.name || ""}
        >
          {asset.name}
        </Link>
        <span className="font-mono text-text-muted block truncate" style={{ fontSize: 11.5, marginTop: 2 }}>
          {asset.slug}
        </span>
      </LibTable.Td>
      <LibTable.Td>
        <span className="block truncate text-text-light" style={{ fontSize: 12.5 }}>
          {asset.analytics_name}
        </span>
      </LibTable.Td>
      <LibTable.NumTd muted>{position}</LibTable.NumTd>
      <LibTable.Td>
        <span className="inline-flex items-center flex-wrap" style={{ gap: 6 }}>
          <StatusPill active={!!asset.is_active} />
          {asset.is_default && <StatusPill tone="navy">Default</StatusPill>}
        </span>
      </LibTable.Td>
      <LibTable.Td style={{ textAlign: "right" }}>
        <span className="inline-flex" style={{ gap: 4 }}>
          {/* Disabled while an order is saving: a delete or edit landing mid-save
              would race the reorder (the save's toast can also close the delete
              confirmation). */}
          <IconButton size="sm" aria-label="Edit" title="Edit" onClick={onEditClick} disabled={disabled}>
            {EditIconSvg}
          </IconButton>
          <IconButton
            size="sm"
            variant="danger"
            aria-label="Delete"
            title="Delete"
            onClick={() => onDelete(asset)}
            disabled={disabled}
          >
            {DeleteIconSvg}
          </IconButton>
        </span>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const PlayerAssetsTable = ({ config }) => {
  const dispatch = useDispatch();
  const assets = useSelector(state => state.player_assets.byType[config.type]) || [];
  const loading = useSelector(state => !!state.player_assets.loadingByType[config.type]);
  const loadError = useSelector(state => state.player_assets.errorByType[config.type]);
  const isMusic = config.fields.audio.show;

  // The action rethrows so callers can react; without a catch here a failed
  // load would also surface as an unhandled rejection.
  const load = useCallback(() => dispatch(fetchPlayerAssets(config.type)).catch(() => {}), [dispatch, config.type]);

  useEffect(() => {
    load();
  }, [load]);

  // Local ordered list — the draggable source of truth. Re-syncs whenever the
  // redux list changes (initial load, retry, or a reorder failure's re-fetch).
  // Seeded from the store so a screen that is already loaded never paints an
  // empty catalog before the sync effect runs.
  const [localList, setLocalList] = useState(assets);
  useEffect(() => {
    setLocalList(assets);
  }, [assets]);

  // True while a drop's PUT is in flight — blocks starting a second drag so
  // two reorders can never race each other.
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      if (!over) return;

      const next = moveAsset(localList, active.id, over.id);
      if (!next) return;

      setLocalList(next);
      setSaving(true);
      dispatch(
        reorderPlayerAssets(
          config.type,
          next.map(asset => asset.id)
        )
      )
        .catch(() => {})
        .finally(() => setSaving(false));
    },
    [localList, dispatch, config.type]
  );

  const ids = useMemo(() => localList.map(asset => asset.id), [localList]);

  const navigate = useNavigate();

  // Single shared <audio> so only one track plays at a time.
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    },
    []
  );

  const togglePreview = asset => {
    if (!asset.audio_url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener("ended", () => setPlayingId(null));
    }
    if (playingId === asset.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current.src = asset.audio_url;
    audioRef.current.currentTime = 0;
    setPlayingId(asset.id);
    const result = audioRef.current.play();
    if (result && typeof result.catch === "function") result.catch(() => setPlayingId(null));
  };

  const handleDelete = asset => {
    Swal.fire({
      title: "Are you sure?",
      text: `You won't be able to revert deleting "${asset.name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(deletePlayerAsset(asset.id))
          .then(() => {
            // The deleted row takes its play/pause button with it: stop its track.
            if (playingId === asset.id && audioRef.current) {
              audioRef.current.pause();
              setPlayingId(null);
            }
            Swal.fire({ icon: "success", title: `${config.singular} Deleted!`, timer: 1000 });
            return load();
          })
          .catch(error => {
            // A server response (e.g. "cannot delete the default asset") was
            // already shown by the shared handler; a lost one was not.
            if (!error?.response) {
              Swal.fire({ icon: "error", title: "Delete not confirmed", text: UNCONFIRMED_MESSAGE });
            }
          });
      }
    });
  };

  if (loading) {
    return <LoadingBackdrop open={true} />;
  }

  // Never show a failed load as an empty catalog: the admin would think the
  // list really is empty, and deleting or re-adding from that state is worse.
  if (loadError) {
    return (
      <div style={{ padding: "0 28px 32px" }}>
        <div
          className="bg-surface border border-sand flex flex-col items-center text-center"
          style={{ borderRadius: 12, padding: "32px 16px", gap: 12 }}
        >
          <p className="text-danger" style={{ fontSize: 13.5, margin: 0 }}>
            {loadError}
          </p>
          <Button variant="primary" onClick={load}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (localList.length === 0) {
    return (
      <div
        className="bg-surface border border-sand text-text-muted italic text-center"
        style={{ borderRadius: 12, padding: "40px 16px", fontSize: 13, margin: "0 28px 32px" }}
      >
        No {config.label.toLowerCase()} available
      </div>
    );
  }

  return (
    <div style={{ padding: "0 28px 32px" }}>
      <p className="text-text-muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
        Drag any row to reorder · saved on each drop · this is the order the app shows
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <LibTable fixed className="border border-sand" style={{ borderRadius: 12, overflow: "hidden" }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 84 }} />
            <col />
            <col style={{ width: "22%" }} />
            <col style={{ width: 56 }} />
            <col style={{ width: 180 }} />
            <col style={{ width: 96 }} />
          </colgroup>
          <LibTable.Head>
            <tr>
              <LibTable.Th aria-label="Drag to reorder"> </LibTable.Th>
              <LibTable.Th>Preview</LibTable.Th>
              <LibTable.Th>Name</LibTable.Th>
              <LibTable.Th>Analytics name</LibTable.Th>
              <LibTable.NumTh>#</LibTable.NumTh>
              <LibTable.Th>Status</LibTable.Th>
              <LibTable.Th style={{ textAlign: "right" }}>Actions</LibTable.Th>
            </tr>
          </LibTable.Head>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <tbody>
              {localList.map((asset, idx) => (
                <SortableAssetRow
                  key={asset.id}
                  asset={asset}
                  type={config.type}
                  position={idx + 1}
                  isMusic={isMusic}
                  playingId={playingId}
                  onTogglePreview={togglePreview}
                  editPath={`${config.basePath}/edit/${asset.id}`}
                  onEditClick={() => navigate(`${config.basePath}/edit/${asset.id}`)}
                  onDelete={handleDelete}
                  disabled={saving}
                />
              ))}
            </tbody>
          </SortableContext>
        </LibTable>
      </DndContext>
    </div>
  );
};

export default PlayerAssetsTable;
